import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import Modal, { ModalBody, ModalFooter, ModalHeader } from "../../components/modal/Modal";
import { useAuthStore } from "../../stores/auth";
import {
  buildTextColumn,
  coerceDocValue,
  collectNewEnumValues,
  defaultDocLeafValue,
  docLeafOptions,
  extendDocColumnEnums,
  genRowId,
  isNewEnumValue,
  matchColumnsToLeaves,
} from "./utils";

const MODE_REPLACE = "replace";
const MODE_FILL = "fill";

const stepsForMode = (mode) => [
  "file",
  "options",
  mode === MODE_FILL ? "mapping" : "columns",
  "preview",
];
const PREVIEW_LIMIT = 10;
const CSV_EXTENSIONS = [".csv", ".tsv", ".txt"];
const DELIMITER_CANDIDATES = [";", "\t", "|", ","];

const isCsvName = (name = "") => {
  const lower = name.toLowerCase();
  return CSV_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

const detectDelimiter = (text) => {
  const firstLine = (text.split(/\r?\n/).find((l) => l.trim().length > 0) || "");
  for (const d of DELIMITER_CANDIDATES) {
    if (firstLine.includes(d)) return d;
  }
  return ",";
};

const readFileAsArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error("read failed"));
    reader.onload = () => resolve(reader.result);
    reader.readAsArrayBuffer(file);
  });

const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error("read failed"));
    reader.onload = () => resolve(reader.result);
    reader.readAsText(file);
  });

export const parseCsvText = (text, delimiter) => {
  const fs = delimiter && delimiter !== "auto" ? delimiter : detectDelimiter(text);
  const workbook = XLSX.read(text, { type: "string", FS: fs, raw: false });
  return workbook;
};

export const parseXlsxBuffer = (buffer) =>
  XLSX.read(buffer, { type: "array", cellDates: true });

const sheetToMatrix = (sheet) =>
  XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: "" });

const stringify = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return String(v);
};

const buildPreview = (matrix, firstRowIsHeaders) => {
  if (!matrix.length) return { headers: [], rows: [], emptyMask: [] };
  const width = matrix.reduce((m, r) => Math.max(m, r.length), 0);
  const headerRow = firstRowIsHeaders ? matrix[0] : null;
  const headers = Array.from({ length: width }, (_, i) =>
    stringify(headerRow?.[i]).trim() || `Column ${i + 1}`,
  );
  const dataStart = firstRowIsHeaders ? 1 : 0;
  const rows = matrix.slice(dataStart).map((r) =>
    Array.from({ length: width }, (_, i) => stringify(r?.[i])),
  );
  // A column is "empty" when every data cell is blank after trim. The header
  // is ignored on purpose — a header without any data is still noise.
  const emptyMask = Array.from({ length: width }, (_, i) =>
    rows.every((r) => stringify(r[i]).trim() === ""),
  );
  return { headers, rows, emptyMask };
};

function WizardSteps({ current, steps, t }) {
  return (
    <div className="nav-wizards-container mb-3">
      <ul className="nav nav-wizards-2">
        {steps.map((step, idx) => {
          let cls = "nav-link disabled";
          if (idx < current) cls = "nav-link completed";
          else if (idx === current) cls = "nav-link active";
          return (
            <li key={step} className="nav-item">
              <span className={cls}>
                <span className="nav-text">
                  {idx + 1}. {t(`delivery_notes.templates.import_excel.step_${step}`)}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function FileStep({ t, file, error, fileInputRef, onPickFile }) {
  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv,.tsv,.txt"
        className="d-none"
        onChange={(e) => onPickFile(e.target.files?.[0] || null)}
        data-testid="import-file-input"
      />
      <button
        type="button"
        className="btn btn-outline-primary w-100 py-4"
        onClick={() => fileInputRef.current?.click()}
      >
        <i className="fa fa-upload me-2" />
        {t("delivery_notes.templates.import_excel.drop_hint")}
      </button>
      {file && (
        <div className="mt-3 text-muted small">
          {t("delivery_notes.templates.import_excel.file_selected", { name: file.name })}
        </div>
      )}
      {error && <div className="mt-3 text-danger small">{error}</div>}
    </>
  );
}

function ModeOption({ t, mode, value, setMode }) {
  const id = `import-mode-${value}`;
  return (
    <div className="form-check">
      <input
        id={id}
        type="radio"
        name="import-mode"
        className="form-check-input"
        checked={mode === value}
        onChange={() => setMode(value)}
      />
      <label className="form-check-label" htmlFor={id}>
        {t(`delivery_notes.templates.import_excel.mode_${value}`)}
        <span className="text-muted d-block small">
          {t(`delivery_notes.templates.import_excel.mode_${value}_hint`)}
        </span>
      </label>
    </div>
  );
}

function OptionsStep({
  t,
  isCsv,
  delimiter,
  setDelimiter,
  firstRowIsHeaders,
  setFirstRowIsHeaders,
  ignoreEmptyColumns,
  setIgnoreEmptyColumns,
  sheetNames,
  sheetName,
  setSheetName,
  canFill,
  mode,
  setMode,
}) {
  return (
    <div className="d-flex flex-column gap-3">
      {canFill && (
        <div>
          <div className="form-label">
            {t("delivery_notes.templates.import_excel.mode_label")}
          </div>
          <ModeOption t={t} mode={mode} value={MODE_FILL} setMode={setMode} />
          <ModeOption t={t} mode={mode} value={MODE_REPLACE} setMode={setMode} />
        </div>
      )}
      {sheetNames.length > 1 && (
        <div>
          <label className="form-label" htmlFor="import-sheet">
            {t("delivery_notes.templates.import_excel.sheet")}
          </label>
          <select
            id="import-sheet"
            className="form-select"
            value={sheetName}
            onChange={(e) => setSheetName(e.target.value)}
          >
            {sheetNames.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      )}
      {isCsv && (
        <div>
          <label className="form-label" htmlFor="import-delimiter">
            {t("delivery_notes.templates.import_excel.delimiter")}
          </label>
          <select
            id="import-delimiter"
            className="form-select"
            value={delimiter}
            onChange={(e) => setDelimiter(e.target.value)}
          >
            <option value="auto">{t("delivery_notes.templates.import_excel.delimiter_auto")}</option>
            <option value=",">{t("delivery_notes.templates.import_excel.delimiter_comma")}</option>
            <option value=";">{t("delivery_notes.templates.import_excel.delimiter_semicolon")}</option>
            <option value={"\t"}>{t("delivery_notes.templates.import_excel.delimiter_tab")}</option>
            <option value="|">{t("delivery_notes.templates.import_excel.delimiter_pipe")}</option>
          </select>
        </div>
      )}
      <div className="form-check">
        <input
          id="import-headers"
          type="checkbox"
          className="form-check-input"
          checked={firstRowIsHeaders}
          onChange={(e) => setFirstRowIsHeaders(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="import-headers">
          {t("delivery_notes.templates.import_excel.use_first_row_as_headers")}
        </label>
      </div>
      <div className="form-check">
        <input
          id="import-ignore-empty"
          type="checkbox"
          className="form-check-input"
          checked={ignoreEmptyColumns}
          onChange={(e) => setIgnoreEmptyColumns(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="import-ignore-empty">
          {t("delivery_notes.templates.import_excel.ignore_empty_columns")}
        </label>
      </div>
    </div>
  );
}

function ColumnsStep({ t, headers, rows, emptyMask, selected, toggleColumn }) {
  if (!headers.length) {
    return (
      <div className="text-muted">
        {t("delivery_notes.templates.import_excel.no_columns")}
      </div>
    );
  }
  const sampleRow = rows.find((r) => r.some((v) => v !== "")) || [];
  return (
    <>
      <div className="text-muted small mb-3">
        {t("delivery_notes.templates.import_excel.select_columns_hint")}
      </div>
      <div className="d-flex flex-column gap-2">
        {headers.map((h, i) => {
          const isEmpty = emptyMask[i];
          return (
            <div key={i} className="form-check">
              <input
                id={`import-col-${i}`}
                type="checkbox"
                className="form-check-input"
                checked={!!selected[i]}
                onChange={() => toggleColumn(i)}
              />
              <label
                className={`form-check-label${isEmpty ? " text-muted" : ""}`}
                htmlFor={`import-col-${i}`}
              >
                <span className="fw-semibold">{h}</span>
                {isEmpty && (
                  <span className="badge bg-secondary bg-opacity-25 text-secondary-emphasis ms-2">
                    {t("delivery_notes.templates.import_excel.empty_column")}
                  </span>
                )}
                {!isEmpty && sampleRow[i] !== undefined && sampleRow[i] !== "" && (
                  <span className="text-muted ms-2">
                    {t("delivery_notes.templates.import_excel.sample_value", {
                      value: sampleRow[i],
                    })}
                  </span>
                )}
              </label>
            </div>
          );
        })}
      </div>
    </>
  );
}

function MappingStep({ t, headers, rows, emptyMask, mapping, setColumnTarget, leafOptions }) {
  if (!headers.length) {
    return (
      <div className="text-muted">
        {t("delivery_notes.templates.import_excel.no_columns")}
      </div>
    );
  }
  const sampleRow = rows.find((r) => r.some((v) => v !== "")) || [];
  const isUsedByOther = (colIdx, leafId) =>
    Object.entries(mapping).some(
      ([otherIdx, target]) => Number(otherIdx) !== colIdx && target === leafId,
    );
  const mappedIds = new Set(Object.values(mapping).filter(Boolean));
  const unmappedFields = leafOptions.filter((o) => !mappedIds.has(o.id));
  return (
    <>
      <div className="text-muted small mb-3">
        {t("delivery_notes.templates.import_excel.mapping_hint")}
      </div>
      <div className="d-flex flex-column gap-2">
        {headers.map((h, i) => {
          const isEmpty = emptyMask[i];
          return (
            <div key={i} className="row g-2 align-items-center">
              <div className="col-6">
                <span className={`fw-semibold${isEmpty ? " text-muted" : ""}`}>{h}</span>
                {isEmpty && (
                  <span className="badge bg-secondary bg-opacity-25 text-secondary-emphasis ms-2">
                    {t("delivery_notes.templates.import_excel.empty_column")}
                  </span>
                )}
                {!isEmpty && sampleRow[i] !== undefined && sampleRow[i] !== "" && (
                  <span className="text-muted small d-block">
                    {t("delivery_notes.templates.import_excel.sample_value", {
                      value: sampleRow[i],
                    })}
                  </span>
                )}
              </div>
              <div className="col-6">
                <select
                  className="form-select form-select-sm"
                  aria-label={h}
                  value={mapping[i] || ""}
                  onChange={(e) => setColumnTarget(i, e.target.value)}
                >
                  <option value="">
                    {t("delivery_notes.templates.import_excel.mapping_skip")}
                  </option>
                  {leafOptions.map((o) => (
                    <option key={o.id} value={o.id} disabled={isUsedByOther(i, o.id)}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          );
        })}
      </div>
      {unmappedFields.length > 0 && (
        <div className="text-muted small mt-3">
          {t("delivery_notes.templates.import_excel.unmapped_fields", {
            fields: unmappedFields.map((o) => o.label).join(", "),
          })}
        </div>
      )}
    </>
  );
}

function PreviewStep({
  t,
  headers,
  rows,
  selected,
  mode,
  mapping,
  leafOptions,
  newEnumValues,
  hasExistingData,
  hasExistingRows,
}) {
  const isFill = mode === MODE_FILL;
  const optionById = new Map(leafOptions.map((o) => [o.id, o]));
  const selectedIdx = headers
    .map((_, i) => i)
    .filter((i) => (isFill ? !!mapping[i] : selected[i]));
  const columnTitle = (i) =>
    isFill ? optionById.get(mapping[i])?.label : headers[i];
  const cellClass = (i, raw) => {
    if (!isFill) return undefined;
    const leaf = optionById.get(mapping[i])?.leaf;
    return leaf && isNewEnumValue(leaf, raw ?? "") ? "table-warning" : undefined;
  };
  const previewRows = rows.slice(0, PREVIEW_LIMIT);
  const newOptionEntries = Object.entries(newEnumValues || {});
  const showReplaceWarning = !isFill && hasExistingData;
  const showFillWarning = isFill && hasExistingRows;
  return (
    <>
      <div className="text-muted small mb-2">
        {t("delivery_notes.templates.import_excel.column_count", { count: selectedIdx.length })}
        {" · "}
        {t("delivery_notes.templates.import_excel.row_count", { count: rows.length })}
      </div>
      <div className="table-responsive border rounded">
        <table className="table table-sm table-striped mb-0">
          <thead>
            <tr>
              {selectedIdx.map((i) => (
                <th key={i}>{columnTitle(i)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {previewRows.map((row, rIdx) => (
              <tr key={rIdx}>
                {selectedIdx.map((i) => (
                  <td key={i} className={cellClass(i, row[i])}>
                    {row[i]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {isFill && newOptionEntries.length > 0 && (
        <div className="alert alert-warning mt-3 mb-0">
          <i className="fa fa-exclamation-triangle me-2" />
          {t("delivery_notes.templates.import_excel.new_options_warning")}
          <ul className="mb-0 mt-1">
            {newOptionEntries.map(([leafId, valueList]) => (
              <li key={leafId}>
                <span className="fw-semibold">{optionById.get(leafId)?.label}</span>
                {": "}
                {valueList.join(", ")}
              </li>
            ))}
          </ul>
        </div>
      )}
      {showReplaceWarning && (
        <div className="alert alert-warning mt-3 mb-0">
          <i className="fa fa-exclamation-triangle me-2" />
          {t("delivery_notes.templates.import_excel.replace_warning")}
        </div>
      )}
      {showFillWarning && (
        <div className="alert alert-warning mt-3 mb-0">
          <i className="fa fa-exclamation-triangle me-2" />
          {t("delivery_notes.templates.import_excel.fill_warning")}
        </div>
      )}
    </>
  );
}

export default function DocumentationImportWizard({
  onCancel,
  onImport,
  hasExistingData = false,
  hasExistingRows = false,
  schema = null,
}) {
  const { t } = useTranslation();
  const me = useAuthStore((state) => state.me);
  const fileInputRef = useRef(null);

  const leafOptions = useMemo(() => docLeafOptions(schema?.columns), [schema]);
  const canFill = leafOptions.length > 0;

  const [step, setStep] = useState(0);
  const [mode, setMode] = useState(canFill ? MODE_FILL : MODE_REPLACE);
  const [file, setFile] = useState(null);
  const [workbook, setWorkbook] = useState(null);
  const [sheetName, setSheetName] = useState("");
  const [delimiter, setDelimiter] = useState("auto");
  const [firstRowIsHeaders, setFirstRowIsHeaders] = useState(true);
  const [ignoreEmptyColumns, setIgnoreEmptyColumns] = useState(true);
  const [selected, setSelected] = useState({});
  const [mapping, setMapping] = useState({});
  const [error, setError] = useState("");

  const isCsv = file ? isCsvName(file.name) : false;

  const sheetNames = workbook?.SheetNames || [];
  const activeSheet = workbook && sheetName ? workbook.Sheets[sheetName] : null;
  const matrix = useMemo(
    () => (activeSheet ? sheetToMatrix(activeSheet) : []),
    [activeSheet],
  );
  const { headers, rows, emptyMask } = useMemo(
    () => buildPreview(matrix, firstRowIsHeaders),
    [matrix, firstRowIsHeaders],
  );

  const parseFile = async (pickedFile, nextDelimiter) => {
    setError("");
    try {
      let wb;
      if (isCsvName(pickedFile.name)) {
        const text = await readFileAsText(pickedFile);
        wb = parseCsvText(text, nextDelimiter);
      } else {
        const buffer = await readFileAsArrayBuffer(pickedFile);
        wb = parseXlsxBuffer(buffer);
      }
      setWorkbook(wb);
      const first = wb.SheetNames[0] || "";
      setSheetName(first);
      return wb;
    } catch (e) {
      setError(t("delivery_notes.templates.import_excel.parse_error"));
      setWorkbook(null);
      setSheetName("");
      return null;
    }
  };

  const onPickFile = async (picked) => {
    setFile(picked);
    setSelected({});
    if (!picked) {
      setWorkbook(null);
      return;
    }
    const wb = await parseFile(picked, delimiter);
    if (wb) setStep(1);
  };

  const onChangeDelimiter = async (next) => {
    setDelimiter(next);
    if (file && isCsv) await parseFile(file, next);
  };

  const steps = stepsForMode(mode);

  const newEnumValues = useMemo(
    () =>
      mode === MODE_FILL
        ? collectNewEnumValues(rows, mapping, leafOptions.map((o) => o.leaf))
        : {},
    [mode, rows, mapping, leafOptions],
  );

  const goNext = () => {
    if (step === 1 && mode === MODE_FILL) {
      // initialize the column → field mapping when entering the mapping step;
      // matching names are pre-assigned, empty columns start skipped when the
      // "ignore empty" option is on
      const next = matchColumnsToLeaves(headers, leafOptions.map((o) => o.leaf));
      headers.forEach((_, i) => {
        if (ignoreEmptyColumns && emptyMask[i]) next[i] = "";
      });
      setMapping(next);
    } else if (step === 1) {
      // initialize column selection when entering the columns step; empty
      // columns start unchecked when the "ignore empty" option is on
      const next = {};
      headers.forEach((_, i) => {
        next[i] = !(ignoreEmptyColumns && emptyMask[i]);
      });
      setSelected(next);
    }
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const toggleColumn = (i) =>
    setSelected((prev) => ({ ...prev, [i]: !prev[i] }));

  const setColumnTarget = (i, leafId) =>
    setMapping((prev) => ({ ...prev, [i]: leafId }));

  const canAdvance = () => {
    if (step === 0) return !!file && !!workbook;
    if (step === 1) return headers.length > 0;
    if (step === 2) {
      if (mode === MODE_FILL) return Object.values(mapping).some(Boolean);
      return Object.values(selected).some(Boolean);
    }
    return true;
  };

  const buildImportedFrom = () => ({
    filename: file?.name || "",
    at: new Date().toISOString(),
    by: me ? { id: me.id, name: me.name || "" } : null,
  });

  const finalizeReplace = () => {
    const selectedIdx = headers.map((_, i) => i).filter((i) => selected[i]);
    const columns = selectedIdx.map((srcIdx, outIdx) =>
      buildTextColumn(headers[srcIdx], outIdx),
    );
    const outRows = rows.map((row) => {
      const obj = { _id: genRowId() };
      selectedIdx.forEach((srcIdx, outIdx) => {
        obj[`f_${outIdx + 1}`] = row[srcIdx] ?? "";
      });
      return obj;
    });
    onImport({ columns, rows: outRows, importedFrom: buildImportedFrom() });
  };

  const finalizeFill = () => {
    // Unknown select/radio values become new options on their fields; rows
    // are then coerced against the extended fields so every cell references
    // a real (possibly just added) option.
    const columns = extendDocColumnEnums(schema?.columns, newEnumValues);
    const extendedOptions = docLeafOptions(columns);
    const leafById = new Map(extendedOptions.map((o) => [o.id, o.leaf]));
    const outRows = rows.map((row) => {
      const obj = { _id: genRowId() };
      extendedOptions.forEach((o) => {
        obj[o.id] = defaultDocLeafValue(o.leaf);
      });
      headers.forEach((_, i) => {
        const leafId = mapping[i];
        if (!leafId) return;
        const leaf = leafById.get(leafId);
        if (!leaf) return;
        const value = coerceDocValue(leaf, row[i] ?? "");
        // Empty cells keep the field default — writing "" into a select
        // would display the first option while actually holding "".
        if (value !== "") obj[leafId] = value;
      });
      return obj;
    });
    onImport({ columns, rows: outRows, importedFrom: buildImportedFrom() });
  };

  const finalize = () => {
    if (mode === MODE_FILL) finalizeFill();
    else finalizeReplace();
  };

  const isLast = step === steps.length - 1;
  const confirmsReplace = mode === MODE_REPLACE && hasExistingData;

  return (
    <Modal size="lg">
      <ModalHeader>
        <h5 className="modal-title">
          {t("delivery_notes.templates.import_excel.title")}
        </h5>
      </ModalHeader>
      <ModalBody>
        <WizardSteps current={step} steps={steps} t={t} />
        {step === 0 && (
          <FileStep
            t={t}
            file={file}
            error={error}
            fileInputRef={fileInputRef}
            onPickFile={onPickFile}
          />
        )}
        {step === 1 && (
          <OptionsStep
            t={t}
            isCsv={isCsv}
            delimiter={delimiter}
            setDelimiter={onChangeDelimiter}
            firstRowIsHeaders={firstRowIsHeaders}
            setFirstRowIsHeaders={setFirstRowIsHeaders}
            ignoreEmptyColumns={ignoreEmptyColumns}
            setIgnoreEmptyColumns={setIgnoreEmptyColumns}
            sheetNames={sheetNames}
            sheetName={sheetName}
            setSheetName={setSheetName}
            canFill={canFill}
            mode={mode}
            setMode={setMode}
          />
        )}
        {step === 2 && mode === MODE_FILL && (
          <MappingStep
            t={t}
            headers={headers}
            rows={rows}
            emptyMask={emptyMask}
            mapping={mapping}
            setColumnTarget={setColumnTarget}
            leafOptions={leafOptions}
          />
        )}
        {step === 2 && mode === MODE_REPLACE && (
          <ColumnsStep
            t={t}
            headers={headers}
            rows={rows}
            emptyMask={emptyMask}
            selected={selected}
            toggleColumn={toggleColumn}
          />
        )}
        {step === 3 && (
          <PreviewStep
            t={t}
            headers={headers}
            rows={rows}
            selected={selected}
            mode={mode}
            mapping={mapping}
            leafOptions={leafOptions}
            newEnumValues={newEnumValues}
            hasExistingData={hasExistingData}
            hasExistingRows={hasExistingRows}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn-default" onClick={onCancel}>
          {t("delivery_notes.templates.import_excel.cancel")}
        </button>
        {step > 0 && (
          <button type="button" className="btn btn-default" onClick={goBack}>
            {t("delivery_notes.templates.import_excel.back")}
          </button>
        )}
        {!isLast && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canAdvance()}
            onClick={goNext}
          >
            {t("delivery_notes.templates.import_excel.next")}
          </button>
        )}
        {isLast && (
          <button
            type="button"
            className={`btn ${confirmsReplace ? "btn-danger" : "btn-primary"}`}
            disabled={!canAdvance()}
            onClick={finalize}
          >
            {confirmsReplace
              ? t("delivery_notes.templates.import_excel.replace")
              : t("delivery_notes.templates.import_excel.import")}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
}
